import { useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * M11 L11.05 — Photo Validation Screen.
 * Displays photos by type with validation checklist.
 */
interface Photo {
  readonly photoId: string;
  readonly photoType: string;
  readonly storageUrl: string;
  readonly capturedAt: string;
  readonly gpsLat: number | null;
  readonly gpsLng: number | null;
  readonly validationStatus: string | null;
  readonly remarks: string | null;
}

export function PhotoValidationPage(): ReactNode {
  const { t: tr } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { atDay } = useBusinessDay();
  const { verificationId } = useParams<{ verificationId: string }>();
  const auth = useAuth();
  const [photos, setPhotos] = useState<Photo[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<Photo[]>(`/v1/closure/review/${verificationId}/photos`, { token: auth.token })
      .then((d) => { if (alive) setPhotos(d); })
      .catch((e) => { if (alive) setError(e?.message ?? 'Failed to load photos'); });
    return () => { alive = false; };
  }, [auth.token, verificationId]);

  if (error) return <main aria-label="photo-validation"><p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p></main>;
  if (!photos) return <main aria-label="photo-validation"><p>{tr('closure.photo.loading')}</p></main>;

  // M11PhotoType, all seven. 'ADDITIONAL' was not one of them, and
  // BRAND_VISIBILITY was missing, so photos of either type were dropped
  // silently: the filter matched no group and nothing rendered them.
  const types = [
    'BEFORE', 'DURING', 'AFTER', 'LOCATION_CONTEXT',
    'REWORK', 'ADDITIONAL_EVIDENCE', 'BRAND_VISIBILITY',
  ];

  return (
    <main aria-label="photo-validation">
      <h2>{tr('closure.photo.title')}</h2>
      <p><Link to={`/closure/final/review/${verificationId}`}>{tr('closure.photo.back')}</Link></p>
      {types.map((t) => {
        const group = photos.filter((p) => p.photoType === t);
        return (
          <section key={t} aria-label={`photo-group-${t}`} style={{ marginBottom: 24 }}>
            <h3>{t.replace(/_/g, ' ')} ({group.length})</h3>
            {group.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)' }}>{tr('closure.photo.none', { type: t.toLowerCase() })}</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {group.map((p) => (
                  <div key={p.photoId} data-testid={`photo-${p.photoId}`}
                       style={{ border: '1px solid var(--color-border-default)', borderRadius: 8, padding: 8, width: 200 }}>
                    <div style={{ height: 120, backgroundColor: 'var(--color-bg-muted)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                      <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>📷 {p.photoType}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                      <div>{tr('closure.photo.captured')}: {atDay(p.capturedAt)}</div>
                      <div>{tr('closure.photo.gps')}: {p.gpsLat != null ? `${p.gpsLat.toFixed(4)}, ${p.gpsLng?.toFixed(4)}` : '—'}</div>
                      <div>{tr('closure.photo.status')}: <strong>{p.validationStatus ?? 'PENDING'}</strong></div>
                      {p.remarks && <div>{tr('closure.photo.remarks')}: {p.remarks}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}

      <section aria-label="photo-checklist" style={{ marginTop: 16, padding: 16, backgroundColor: 'var(--color-bg-muted)', borderRadius: 8 }}>
        <h3>{tr('closure.photo.checklistH')}</h3>
        <ul>
          {['Brand Visibility', 'Logo Placement', 'Color Compliance', 'Execution Quality',
            'Clean Finish', 'Site Suitability', 'Photo Clarity', 'Photo Completeness'].map((c) => (
            <li key={c} data-testid={`checklist-${c.replace(/\s/g, '-').toLowerCase()}`}>{c}</li>
          ))}
        </ul>
      </section>
    </main>
  );
}
